// Copyright TraderEvolution Global LTD. © 2017-2024. All rights reserved.

import { CustomEvent } from "../../../Utils/CustomEvents.ts";
import { ErrorInformationStorage } from "../../../Commons/ErrorInformationStorage.ts";
import { Rectangle } from "../../../Commons/Geometry.ts";
import { ControlsUtils } from "../../UtilsClasses/ControlsUtils.js";
import { QuickTableRactiveTemplate } from "../../../templates.js";
import { RactiveTooltip } from "../../misc/Decorators.js";
import { Control } from "../Control.js";
import { QuickTableNumeric } from "../QuickTableNumeric.js";
import { QuickTableComboBox } from "../QuickTableComboBox.js";
import { QuickTableTifComboBox } from "../QuickTableTifComboBox.js";
import { TerceraQuickTree } from "../QuickTree/TerceraQuickTree.js";
import { QuickTable } from "./QuickTable.js";
import { PlacedFrom } from "../../../Utils/Trading/PlacedFrom.ts";
import { ThemeManager } from "../../misc/ThemeManager.js";
import { KeyEventProcessor } from "../../../Commons/KeyEventProcessor.ts";
import { DynProperty } from "../../../Commons/DynProperty.ts";
import { GeneralSettings } from "../../../Utils/GeneralSettings/GeneralSettings.ts";
import { QuickTableAccountLookup } from "../QuickTableAccountLookup.ts";
import { QuickTableInstrumentLookup } from "../QuickTableInstrumentLookup.ts";
import { MainWindowManager } from "../../UtilsClasses/MainWindowManager.js";
import { MathUtils } from "../../../Utils/MathUtils.ts";

export let QuickTableRactive = Control.extend({
    template: QuickTableRactiveTemplate,
    data: function ()
    {
        return {
            id: 'ds',
            zIndex: null,//for lookup
            tableBackColor: 'black',
            currentCursor: 'default',
            autocompleteTable: false
        }
    },
    quickTable: null,

    tooltipKey: null,
    lastShowTooltipTimeoutId: null,

    prevWidth: 0,
    prevHeight: 0
});

QuickTableRactive.prototype.getType = function () { return 'QuickTableRactive' };

//
// Static field
//
QuickTableRactive.staticID = 0;


QuickTableRactive.prototype.oninit = function ()
{
    QuickTableRactive.staticID++;

    this.set('id', 'quicktable' + QuickTableRactive.staticID);

    var keyProc = KeyEventProcessor;
    keyProc.OnKeyDown.Subscribe(this.onGlobalKeyDown, this);
    keyProc.OnKeyUp.Subscribe(this.onGlobalKeyUp, this);

    this.GlobalKeyDownHandled = new CustomEvent();

    this.onEditingResult = this.onEditingResult.bind(this);
};

QuickTableRactive.prototype.onrender = function ()
{
    try
    {
        const canvas = $('#' + this.get('id'))[0];
        var ctx = canvas.getContext('2d', {alpha: false});
        this.quickTable = new QuickTable(ctx);
        this.quickTable.OnShowTooltip.Subscribe(this.onShowTooltip, this);

        this.observe('width', function (newValue, oldValue, keypath)
        {
            if (isValidNumber(newValue)) {
                const devicePixelRatio = window.devicePixelRatio || 1;
                this.quickTable.width = newValue;
                canvas.width = this.quickTable.width * devicePixelRatio;
                ctx.scale(devicePixelRatio, devicePixelRatio);
            } else {
                this.quickTable.width = newValue;
            }
            
            this.quickTable.onResize();
            if (isValidNumber(newValue) && newValue !== 0 && this.prevWidth !== newValue) {
                this.prevWidth = newValue;
                this.removeEditableControl();
            } 
        });
        this.observe('height', function (newValue, oldValue, keypath)
        {
            if (isValidNumber(newValue)) {
                const devicePixelRatio = window.devicePixelRatio || 1;
                this.quickTable.height = newValue;
                canvas.height = this.quickTable.height * devicePixelRatio;
                ctx.scale(devicePixelRatio, devicePixelRatio);
            } else {
                this.quickTable.height = newValue;
            }

            this.quickTable.onResize();
            if (isValidNumber(newValue) && newValue !== 0 && this.prevHeight !== newValue) {
                this.prevHeight = newValue;
                this.removeEditableControl();
            }
        });

        this.quickTable.OnExternalCellEditRequest.Subscribe(this.onExternalCellEditRequest, this);
        // TODO. Wrong concept.
        this.quickTable.OnExternalShowTooltipRequest.Subscribe(this.onExternalShowTooltipRequest, this);

        this.themeChange();
    }
    catch (ex)
    {
        ErrorInformationStorage.GetException(ex);
        this.quickTable = new QuickTable(null);
    }
};

QuickTableRactive.prototype.onExternalCellEditRequest = function (data)
{
    if (!data) return;

    var cell = data.cell;
    if (!cell) return;

    var qtEditingInfo = cell.QuickTableEditingInfo;
    if (!qtEditingInfo) return;

    const absoluteLocation = ControlsUtils.getAbsoluteLocation(this);
    const cellRect = this.quickTable.getCellRectangleRelativeToCanvas(data.row, data.columnIndex);
    const controlRect = new Rectangle(
        absoluteLocation.X + cellRect.X,
        absoluteLocation.Y + cellRect.Y,
        cellRect.Width,
        cellRect.Height);

    switch (qtEditingInfo.ControlType)
    {
        case DynProperty.DOUBLE:
            data.control = new QuickTableNumeric(data, this.onEditingResult, controlRect);
            break;
        case DynProperty.COMBOBOX:
            data.control = new QuickTableComboBox(data, this.onEditingResult, controlRect);
            break;
        case DynProperty.COMBOBOX_COMBOITEM_TIF:
            data.control = new QuickTableTifComboBox(data, this.onEditingResult, controlRect);
            break;
        case  DynProperty.ACCOUNT:
            data.control = new QuickTableAccountLookup(data, this.onEditingResult, controlRect);
            break;
        case DynProperty.INSTRUMENT:
            data.control = new QuickTableInstrumentLookup(data, this.onEditingResult, controlRect);
            break;
    }

    if (data.control) {
        this.quickTable.isActiveEditControl = true;
        MainWindowManager.MainWindow.Controls.quickTableControlContainer.addControl(data.control);
    }
};

QuickTableRactive.prototype.removeEditableControl = function () {
    if (!this.quickTable.isActiveEditControl) {
        return;
    }
    MainWindowManager.MainWindow.Controls.quickTableControlContainer.removeActiveControl();
    MainWindowManager.MainWindow.Controls.quickTableControlContainer.removeParentActiveControl();
    MainWindowManager.MainWindow.Controls.quickTableControlContainer.removeAllControls();
    this.setFocus();
    this.quickTable.isActiveEditControl = false;
}

// TODO. Duplicate of RactiveTooltip.decorator = function (node, text).
QuickTableRactive.prototype.onExternalShowTooltipRequest = function (tooltipKey)
{
    if (tooltipKey && this.tooltipKey === tooltipKey)
        return;

    this.tooltipKey = tooltipKey;

    clearTimeout(this.lastShowTooltipTimeoutId);
    RactiveTooltip.setTooltipVisibility(false);

    if (!GeneralSettings.View.Tooltips || !tooltipKey)
        return;

    this.lastShowTooltipTimeoutId = setTimeout(function (tooltipKey)
    {
        RactiveTooltip.showTooltip(tooltipKey);
    }, 500, tooltipKey);
};

QuickTableRactive.prototype.onEditingResult = function (value, data)
{
    if (!MathUtils.IsNullOrUndefined(value)) {
        this.quickTable.afterExternalCellEdit(value, data);
    }
    this.removeEditableControl();
};

QuickTableRactive.prototype.setShowColumnHeaders = function (show)
{
    this.quickTable.setShowColumnHeaders(show);
};

QuickTableRactive.prototype.onResizeBegin = function ()
{
    this.quickTable.onResizeBegin();
};

QuickTableRactive.prototype.onResizeEnd = function ()
{
    this.quickTable.onResizeEnd();
};

QuickTableRactive.prototype.setSizes = function ()
{
    let panel = this.find("*");
    let w = panel.offsetWidth;
    let h = panel.offsetHeight;

    this.set({ width: w, height: h });
};

QuickTableRactive.prototype.resetSizes = function ()
{
    return this.set({ width: 0, height: 0 });
};

QuickTableRactive.prototype.setBounds = function (x, y, width, height)
{

    if (this.quickTable.lazyResizing)
        Control.prototype.setBounds.apply(this, [x, y, width, height]);
    else
    {

        // Save image before resize and draw it after to avoid flickering (for goo performance)
        //var imgD = this.quickTable.context.getImageData(0, 0, this.quickTable.context.canvas.width - 1, this.quickTable.context.canvas.height - 1);

        Control.prototype.setBounds.apply(this, [x, y, width, height]);

        //this.quickTable.context.putImageData(imgD, 0, 0);

        this.quickTable.needRedrawBackground = true;
        this.quickTable.needRedraw = true;
    }
};

QuickTableRactive.prototype.enableRowHover = function (enableRowHover)
{
    this.quickTable.enableRowHover = enableRowHover;
};

QuickTableRactive.prototype.onClick = function (event)
{
    this.fire(QuickTableRactive.Events.Click, this, event.original);
};

QuickTableRactive.prototype.onMouseDown = function (event)
{
    Control.prototype.onMouseDown.call(this, event);
    this.quickTable.onMouseDown(event.original);
    this.fire(QuickTableRactive.Events.MouseDown, this, event.original);
    ControlsUtils.Capture.SetCapture(this, event.original);
};

QuickTableRactive.prototype.onMouseMove = function (event)
{
    if (this.captured && event.original)
        return;
    Control.prototype.onMouseMove.call(this, event);
    this.quickTable.onMouseMove(event.original || event);

    this.set('currentCursor', this.quickTable.currentCursor);
};

QuickTableRactive.prototype.onMouseLeave = function (event)
{
    Control.prototype.onMouseLeave.call(this, event);
    this.quickTable.onMouseLeave(event.original || event);
};

QuickTableRactive.prototype.onMouseUp = function (event)
{
    if (this.captured && event.original)
        return;
    Control.prototype.onMouseUp.call(this, event);
    this.quickTable.onMouseUp(event.original || event);

    this.set('currentCursor', this.quickTable.currentCursor);
};

QuickTableRactive.prototype.onMouseWheel = function (event)
{
    this.quickTable.onMouseWheel(event);
};

QuickTableRactive.prototype.onDoubleClick = function (event)
{
    var hittestInfo = this.quickTable.HitTest(event.original.offsetX, event.original.offsetY);
    if (hittestInfo.zone == QuickTable.QuickTableZone.Header)
        return;
    Control.prototype.onDoubleClick.call(this, event);
    this.fire(TerceraQuickTree.Events.DoubleClick, PlacedFrom.WEB_ORDERS_PANEL_DB_CLICK, this);
};

QuickTableRactive.prototype.onGlobalKeyDown = function ()
{
    if (!this.get('focused')) return;

    this.quickTable.onKeyDown();
    this.GlobalKeyDownHandled.Raise();
};

QuickTableRactive.prototype.onGlobalKeyUp = function ()
{
    if (!this.get('focused')) return;

    this.quickTable.onKeyUp();
};

QuickTableRactive.prototype.onShowTooltip = function (tooltip)
{
    RactiveTooltip.showTooltip(tooltip);
}

//
//
//
QuickTableRactive.prototype.onteardown = function ()
{
    if (this.quickTable != undefined)
    {
        this.quickTable.OnShowTooltip.UnSubscribe(this.onShowTooltip, this);
        this.quickTable.Dispose();
    }

    var keyProc = KeyEventProcessor;
    keyProc.OnKeyDown.UnSubscribe(this.onGlobalKeyDown, this);
    keyProc.OnKeyUp.UnSubscribe(this.onGlobalKeyUp, this);
}

QuickTableRactive.prototype.themeChange = function ()
{
    if (this.quickTable)
        this.quickTable.themeChange();

    this.set('tableBackColor', ThemeManager.CurrentTheme.quickTableBackColor);
};

QuickTableRactive.prototype.redraw = function () {
    this.quickTable.redraw();
}

QuickTableRactive.Events =
{
    MouseDown: 'MouseDown',
    Click: 'Click',
    DoubleClick: 'DoubleClick'
};
Object.freeze(QuickTableRactive.Events);